﻿import React, { useState, useRef, useEffect } from 'react';
import {
  History as HistoryIcon } from 'lucide-react';
import { Header } from './components/Header';
import { Transcript } from './components/Transcript';
import { Composer } from './components/Composer';
import { ThreadsDrawer } from './components/ThreadsDrawer';
import { PatientSheet } from './components/shell/PatientSheet';
import { SettingsModal } from './components/SettingsModal';
import { PatientPickerModal } from './components/PatientPickerModal';
import { ExportBuilder } from './components/ExportBuilder';
import { ExportCsvBuilder } from './components/ExportCsvBuilder';
import { ReviseNoteModal } from './components/ReviseNoteModal';
import { AssessmentTrendBuilder } from './components/AssessmentTrendBuilder';
import { PromptGallery } from './components/PromptGallery';
import { QuickSelectionPalette, DRAFT_NOTE_OPTIONS, DRAFT_PLAN_OPTIONS } from './components/QuickSelectionPalette';
import { ThemeProvider } from './context/ThemeContext';
import { TooltipProvider } from './components/ui/tooltip';
import { toast } from 'sonner';
import { isBackendCommand, UI_ONLY_BASE_COMMANDS } from './data/commands';
import { isCommandApiEnabled, checkCommandApiHealth, fetchAssessments, fetchAlerts, fetchRedFlags, fetchExports, fetchSimulations, fetchVisits, fetchDigitalTwinOverview, fetchDigitalTwinReplay, type DigitalTwinOverview, type DigitalTwinSnapshot, type EntityListItem } from './api/commandApi';
import { isSmartModeActive } from './api/smartApi';
import { getTenantId } from './auth/iamAccess';
import { buildPatientContextBridge } from './api/buildPatientContextBridge';
import { useThreads } from './hooks/useThreads';
import { useSavedPrompts } from './hooks/useSavedPrompts';
import { useTrafficStreamOptional } from './contexts/TrafficStreamContext';
import { useActivePatient } from './contexts/ActivePatientContext';
import type { MessageWithDate } from './services/threadService';
import { useCommandStream } from './hooks/useCommandStream';


export default function App({ embedded = false }: { embedded?: boolean } = {}) {
  const thread = useThreads();
  const savedPrompts = useSavedPrompts();
  const trafficStream = useTrafficStreamOptional();
  /** Which left sidebar panel is open: conversations, tools, or settings. null = sidebar closed */
  const [sidebarPanel, setSidebarPanel] = useState<'threads' | 'patient' | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [patientPickerOpen, setPatientPickerOpen] = useState(false);
  const [patientPickerForNewThread, setPatientPickerForNewThread] = useState(false);
  const [exportsList, setExportsList] = useState<Array<{ id: string; filename: string; time: string; size: string; type: string }>>([]);
  const [inputValue, setInputValue] = useState('');
  const [currentPatient, setCurrentPatient] = useState<{
    name: string;
    mrn: string;
    status: string;
    risk: string;
  }>({ name: '', mrn: '', status: '', risk: '' });

  // Publish the open chart so the globally-mounted Job Center drawer can scope
  // itself to this patient. Selection here is a click inside the workspace and
  // never reaches the URL, so the drawer has no other way to learn it.
  const { setActivePatient } = useActivePatient();
  const activePatientMrn = currentPatient.mrn.trim();
  const activePatientName = currentPatient.name.trim();
  useEffect(() => {
    setActivePatient(activePatientMrn ? { patientId: activePatientMrn, patientName: activePatientName } : null);
  }, [activePatientMrn, activePatientName, setActivePatient]);
  const [settings, setSettings] = useState<import('./components/SettingsModal').ClinicianSettings>({
    density: 'standard',
    evidenceFooters: true,
  });
  const [commandApiStatus, setCommandApiStatus] = useState<'idle' | 'checking' | 'connected' | 'disconnected'>('idle');
  const [commandLoading, setCommandLoading] = useState(false);
  const [pendingCommand, setPendingCommand] = useState<string | null>(null);

  const [exportBuilderOpen, setExportBuilderOpen] = useState(false);
  const [exportCsvBuilderOpen, setExportCsvBuilderOpen] = useState(false);
  const [reviseNoteOpen, setReviseNoteOpen] = useState(false);
  const [assessmentTrendBuilderOpen, setAssessmentTrendBuilderOpen] = useState(false);
  const [draftNoteOptionsOpen, setDraftNoteOptionsOpen] = useState(false);
  const [draftPlanOptionsOpen, setDraftPlanOptionsOpen] = useState(false);
  const [simulationPresetsOpen, setSimulationPresetsOpen] = useState(false);
  const [promptGalleryOpen, setPromptGalleryOpen] = useState(false);
  const [alertsOptionsOpen, setAlertsOptionsOpen] = useState(false);
  /** All entity lists from backend (no hardcoded data when API enabled). */
  const [assessmentsFromApi, setAssessmentsFromApi] = useState<EntityListItem[]>([]);
  const [alertsFromApi, setAlertsFromApi] = useState<EntityListItem[]>([]);
  const [redFlagsFromApi, setRedFlagsFromApi] = useState<EntityListItem[]>([]);
  const [exportsFromApi, setExportsFromApi] = useState<EntityListItem[]>([]);
  const [simulationsFromApi, setSimulationsFromApi] = useState<EntityListItem[]>([]);
  const [visitsFromApi, setVisitsFromApi] = useState<EntityListItem[]>([]);
  const [entityFetchError, setEntityFetchError] = useState<string | null>(null);
  const [digitalTwinOverview, setDigitalTwinOverview] = useState<DigitalTwinOverview | null>(null);
  const [digitalTwinReplay, setDigitalTwinReplay] = useState<DigitalTwinSnapshot[]>([]);
  const [digitalTwinLoading, setDigitalTwinLoading] = useState(false);
  const [digitalTwinError, setDigitalTwinError] = useState<string | null>(null);

  const appShellRef = useRef<HTMLDivElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<MessageWithDate[]>([]);
  // updateAssistantMessage builds every streaming update FROM this ref; if it
  // ever goes stale the next update REPLACES the whole thread with stale
  // contents. The sync effect below was accidentally deleted in the top-bar
  // shell refactor (#506) together with the sidebar state block next to it —
  // messagesRef stayed [] forever and the first stream event wiped the
  // conversation ("message shows loading but never renders").
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setIsMobileViewport(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);



  // Fetch all entity lists from backend when patient is set and Command API is enabled (no hardcoded data)
  useEffect(() => {
    if (!isCommandApiEnabled() || !currentPatient.mrn?.trim()) {
      setAssessmentsFromApi([]);
      setAlertsFromApi([]);
      setRedFlagsFromApi([]);
      setExportsFromApi([]);
      setSimulationsFromApi([]);
      setVisitsFromApi([]);
      setEntityFetchError(null);
      return;
    }
    // Tenant is resolved by the backend (/auth/me → IAM profile); fall back to
    // the single-tenant default only when the profile hasn't loaded (dev / unauth).
    const tenantId = getTenantId() || 'default';
    const patientId = currentPatient.mrn.trim();
    setEntityFetchError(null);
    let cancelled = false;
    Promise.all([
      fetchAssessments(tenantId, patientId),
      fetchAlerts(tenantId, patientId),
      fetchRedFlags(tenantId, patientId),
      fetchExports(tenantId, patientId),
      fetchSimulations(tenantId, patientId),
      fetchVisits(tenantId, patientId),
    ])
      .then(([assessments, alerts, redFlags, exports, simulations, visits]) => {
        if (!cancelled) {
          setAssessmentsFromApi(assessments);
          setAlertsFromApi(alerts);
          setRedFlagsFromApi(redFlags);
          setExportsFromApi(exports);
          setSimulationsFromApi(simulations);
          setVisitsFromApi(visits);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Failed to load data from the API.';
          setEntityFetchError(msg);
          setAssessmentsFromApi([]);
          setAlertsFromApi([]);
          setRedFlagsFromApi([]);
          setExportsFromApi([]);
          setSimulationsFromApi([]);
          setVisitsFromApi([]);
        }
      });
    return () => { cancelled = true; };
  }, [currentPatient.mrn]);

  useEffect(() => {
    if (!isCommandApiEnabled() || !currentPatient.mrn?.trim()) {
      setDigitalTwinOverview(null);
      setDigitalTwinReplay([]);
      setDigitalTwinLoading(false);
      setDigitalTwinError(null);
      return;
    }
    let cancelled = false;
    setDigitalTwinLoading(true);
    setDigitalTwinError(null);
    Promise.allSettled([
      fetchDigitalTwinOverview(currentPatient.mrn.trim(), thread.currentThreadId || undefined),
      fetchDigitalTwinReplay(currentPatient.mrn.trim(), 12),
    ])
      .then(([overviewResult, replayResult]) => {
        if (cancelled) return;

        const overview =
          overviewResult.status === 'fulfilled'
            ? overviewResult.value
            : {
                available: false,
                summary: '',
                patient: null,
                risk: null,
                workflow: null,
                recommendation: null,
              };
        const replay = replayResult.status === 'fulfilled' ? replayResult.value : [];

        setDigitalTwinOverview(overview);
        setDigitalTwinReplay(replay);

        const errors: string[] = [];
        if (overviewResult.status === 'rejected') {
          errors.push('Digital Twin overview is temporarily unavailable.');
        }
        if (replayResult.status === 'rejected') {
          errors.push('Timeline could not be loaded.');
        }
        setDigitalTwinError(errors.length > 0 ? errors.join(' ') : null);
      })
      .finally(() => {
        if (!cancelled) {
          setDigitalTwinLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentPatient.mrn, thread.currentThreadId]);

  // Keep currentPatient in sync with the selected conversation so sending a command always has the right patient.
  // Fixes "Select a patient first" when user selects a thread that already has a patient (or after reload).
  useEffect(() => {
    const t = thread.currentThread;
    if (!t) return;
    const mrn = t.patientId?.trim() ?? '';
    const name = (t.patientName?.trim() ?? '').replace(/^No patient selected$/i, '');
    if (!mrn && !name) return;
    setCurrentPatient((prev) => {
      const sameMrn = prev.mrn === mrn;
      if (sameMrn && prev.mrn) return prev; // preserve status/risk from picker when same patient
      return {
        name: name || prev.name,
        mrn,
        status: sameMrn ? prev.status : '',
        risk: sameMrn ? prev.risk : '',
      };
    });
  }, [thread.currentThreadId, thread.currentThread?.id, thread.currentThread?.patientId, thread.currentThread?.patientName]);

  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [thread.messages]);

  const handleCreateThread = () => {
    thread.createThread({ patientName: currentPatient.name?.trim() || undefined, patientId: currentPatient.mrn || undefined });
    setInputValue('');
  };

  const handleCreateThreadForPatient = (patient: any) => {
    if (!patient?.name) return;
    setCurrentPatient({
      name: patient.name,
      mrn: patient.mrn,
      status: patient.status ?? '',
      risk: patient.risk ?? '',
    });
    thread.createThread({ patientName: patient.name, patientId: patient.mrn });
    setInputValue('');
  };

  const handleSelectThread = (threadId: string) => {
    if (threadId === thread.currentThreadId) return; // already on this conversation; avoid clearing input
    thread.selectThread(threadId);
    setInputValue('');
  };

  // Command API health: check on mount and when settings open; re-check every 60s
  const runHealthCheck = React.useCallback(() => {
    if (!isCommandApiEnabled()) return;
    setCommandApiStatus((s) => (s === 'idle' ? 'checking' : s));
    checkCommandApiHealth()
      .then((r) => setCommandApiStatus(r.ok ? 'connected' : 'disconnected'))
      .catch(() => setCommandApiStatus('disconnected'));
  }, []);
  useEffect(() => {
    if (!isCommandApiEnabled()) return;
    runHealthCheck();
  }, [runHealthCheck]);
  useEffect(() => {
    if (!isCommandApiEnabled() || !settingsOpen) return;
    runHealthCheck();
  }, [settingsOpen, runHealthCheck]);
  // Adaptive heartbeat: a cold first-load request (DNS + TLS + a warming backend)
  // can exceed the health timeout and momentarily read "offline". Retry quickly
  // while not connected so that transient state self-heals in seconds instead of
  // latching until the next 60s tick (or a manual refresh); settle to a slow
  // heartbeat once healthy.
  useEffect(() => {
    if (!isCommandApiEnabled()) return;
    const delay = commandApiStatus === 'connected' ? 60_000 : 5_000;
    const t = setInterval(runHealthCheck, delay);
    return () => clearInterval(t);
  }, [runHealthCheck, commandApiStatus]);
  // Re-check the instant the network returns or the tab is refocused, so a
  // backgrounded/asleep session reconnects without waiting for the next tick.
  useEffect(() => {
    if (!isCommandApiEnabled()) return;
    const onWake = () => runHealthCheck();
    window.addEventListener('online', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      window.removeEventListener('online', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [runHealthCheck]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Every overlay dialog is Radix-owned and handles Escape through its own
      // dismissable layer stack (topmost closes first when they stack). Only
      // the SimulationBuilder — an inline composer panel, not a dialog — is
      // dismissed here.
      if (e.key === 'Escape' && simulationPresetsOpen) {
        setSimulationPresetsOpen(false);
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [simulationPresetsOpen]);

  useEffect(() => {
    messagesRef.current = thread.messages;
  }, [thread.messages]);

  const updateAssistantMessage = React.useCallback(
    (threadId: string, messageId: string, updater: (m: MessageWithDate) => MessageWithDate) => {
      const next = messagesRef.current.map((m) => (m.id === messageId ? updater(m) : m));
      messagesRef.current = next;
      thread.setThreadMessages(threadId, next);
    },
    [thread]
  );

  const isPlaceholderFinalText = React.useCallback((value: string) => {
    const normalized = value.trim().toLowerCase();
    return (
      normalized === 'plan completed' ||
      normalized === 'plan completed.' ||
      normalized === 'request completed' ||
      normalized === 'request completed.'
    );
  }, []);

  // UX-02 slice 3: the submit→stream orchestration lives in the hook; App keeps
  // thread/message assembly and the exports-drawer side effects it owns.
  const handleExportArtifacts = React.useCallback((widget: { type: string; data: Record<string, unknown> }) => {
    if (widget.type === 'W_EXPORT_LIST' && widget.data?.exports) {
      const list = Array.isArray(widget.data.exports) ? widget.data.exports : [];
      setExportsList(prev => {
        const byId = new Map(prev.map(e => [e.id, e]));
        list.forEach((e: { exportId?: string; filename?: string; timestamp?: string; type?: string; size?: string }) => {
          const id = (e.exportId || e.filename || '').toString();
          if (id) byId.set(id, {
            id,
            filename: (e.filename ?? id).toString(),
            time: (e.timestamp ?? '').toString(),
            size: (e.size ?? '—').toString(),
            type: (e.type ?? 'pdf').toString()
          });
        });
        return Array.from(byId.values());
      });
    }
    if (widget.type === 'W_FILE_PDF' && widget.data) {
      const d = widget.data as { exportId?: string; filename?: string; timestamp?: string };
      const id = (d.exportId || d.filename || `exp-${Date.now()}`).toString();
      setExportsList(prev => [...prev.filter(e => e.id !== id), {
        id,
        filename: (d.filename ?? id).toString(),
        time: (d.timestamp ?? new Date().toLocaleString()).toString(),
        size: '—',
        type: 'pdf'
      }]);
    }
  }, []);

  const { startCommandStream, stopStreaming: handleStopStreaming } = useCommandStream({
    addMessage: thread.addMessage,
    updateAssistantMessage,
    setCommandLoading,
    setPendingCommand,
    pushMarchSseEvent: trafficStream?.pushMarchSseEvent,
    onExportArtifacts: handleExportArtifacts,
    isPlaceholderFinalText,
    currentThreadId: thread.currentThreadId,
  });


  const handleSendMessage = (message: string, options?: Record<string, unknown>) => {
    if (!message.trim()) return;

    const trimmed = message.trim();
    const baseCommand = trimmed.toLowerCase().split(/\s+/)[0];
    const normalizedBase = baseCommand.startsWith('/') ? baseCommand : '/' + baseCommand;
    let threadId = thread.currentThreadId;
    if (!threadId) {
      threadId = `thread-${Date.now()}`;
      thread.createThread({
        patientName: currentPatient.name || undefined,
        patientId: currentPatient.mrn || undefined,
        id: threadId,
      });
    }

    // UI-only commands: handle locally, do not send to backend
    if (UI_ONLY_BASE_COMMANDS.has(normalizedBase)) {
      if (normalizedBase === '/clear') {
        thread.setThreadMessages(threadId, []);
        return;
      }
      if (normalizedBase === '/new_thread') {
        handleCreateThread();
        return;
      }
      if (normalizedBase === '/help' || normalizedBase === '/shortcuts') {
        thread.addMessage(threadId, {
          id: `msg-${Date.now()}`,
          role: 'user',
          content: message,
          timestamp: new Date()
        } as MessageWithDate);
        thread.addMessage(threadId, {
          id: `msg-${Date.now()}-help`,
          role: 'assistant',
          answer: normalizedBase === '/help' ? 'Type / for commands. Summary, Alerts, Risk factors, and more are in the quick prompts. Use Settings to configure the Command API.' : 'Keyboard shortcuts: Enter to send, Escape to close modals.',
          timestamp: new Date()
        } as MessageWithDate);
        setInputValue('');
        return;
      }
    }

    const userMsg: MessageWithDate = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: new Date()
    };
    const isFirstMessageInThread = thread.messages.length === 0;
    thread.addMessage(threadId, userMsg);
    if (isFirstMessageInThread) thread.renameThreadFromFirstMessage(threadId, trimmed);
    setInputValue('');

    const isCommand = trimmed.toLowerCase().startsWith('/');
    const isBackendStyleCommand = isCommand && isBackendCommand(normalizedBase);
    const useRealApi = isCommandApiEnabled() && (!isCommand || isBackendStyleCommand);

    if (useRealApi) {
      const tenantId = getTenantId() || 'default';
      // Source of truth for command routing is the active thread's patient.
      // Fallback to currentPatient for in-flight UI state before thread sync settles.
      const patientId =
        thread.currentThread?.patientId?.trim() ||
        currentPatient.mrn?.trim() ||
        '';
      const patientContextBridge = buildPatientContextBridge({
        patientId,
        threadId,
        currentPatient,
        digitalTwinOverview,
        assessments: assessmentsFromApi,
        alerts: alertsFromApi,
        redFlags: redFlagsFromApi,
        visits: visitsFromApi,
        simulations: simulationsFromApi,
      });
      const bridgedOptions: Record<string, unknown> = {
        ...(options ?? {}),
        patient_context: patientContextBridge,
        patient_context_summary: patientContextBridge.summary,
      };
      startCommandStream({
        message,
        threadId,
        tenantId,
        patientId,
        options: bridgedOptions,
      });
      return;
    }

    // Dev-only mock path: local `vite dev` with no backend, for pure UI work.
    // import.meta.env.DEV is statically false in EVERY build (dev/staging/uat/
    // prod), so this branch and the mock module tree-shake out of the bundle — a
    // built or misconfigured app must never fabricate clinical-looking content.
    if (import.meta.env.DEV) {
      void import('./data/mockData').then(({ addMockMessage }) => {
        setTimeout(() => {
          thread.addMessage(threadId, addMockMessage(message) as MessageWithDate);
        }, 500);
      });
      return;
    }

    // Built app: the assistant is unreachable, or the command was not recognized.
    // Surface it plainly and never generate a clinical answer.
    thread.addMessage(threadId, {
      id: `msg-${Date.now()}-unavailable`,
      role: 'assistant',
      answer: isCommandApiEnabled()
        ? `"${trimmed}" isn't a recognized command. Type / to see the available commands.`
        : 'The clinical assistant is not reachable right now, so no response can be shown. The app may be misconfigured or the service may be unavailable — please retry, or contact your administrator. No data was generated.',
      timestamp: new Date(),
      retryCommand: message.trim(),
    } as MessageWithDate);
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const handlePromptFromGallery = (promptText: string) => {
    // Route command-style prompts through quick prompt handling where applicable
    const labelMap: Record<string, string> = {
      '/summary': 'Summary',
      '/alerts': 'Alerts',
      '/risk_profile': 'Risk factors',
      '/twin_snapshot': 'Twin snapshot',
      '/simulate': 'Run simulation',
      '/draft_note': 'Draft note',
      '/draft_plan': 'Draft plan',
      '/export_pdf': 'Export PDF',
    };
    const trimmed = promptText.trim();
    const label = labelMap[trimmed.split(/\s+/)[0]];
    if (label) {
      handleQuickPrompt(label);
    } else {
      handleSendMessage(trimmed);
    }
  };

  const handleQuickPrompt = (promptLabel: string) => {
    // Handle chips that need Level-2 palettes
    if (promptLabel === 'Export PDF') {
      setExportBuilderOpen(true);
      return;
    }
    
    if (promptLabel === 'Draft note') {
      setDraftNoteOptionsOpen(true);
      return;
    }
    
    if (promptLabel === 'Draft plan') {
      setDraftPlanOptionsOpen(true);
      return;
    }
    
    if (promptLabel === 'Run simulation') {
      setSimulationPresetsOpen(true);
      return;
    }
    
    if (promptLabel === 'Alerts') {
      setAlertsOptionsOpen(true);
      return;
    }
    
    if (promptLabel === 'Summary') {
      handleSendMessage('/summary');
      return;
    }
    
    // Map quick prompt labels to their commands for auto-send chips
    const commandMap: Record<string, string> = {
      'Latest assessment': '/latest_assessment',
      'Risk factors': '/risk_profile',
      'Twin snapshot': '/twin_snapshot'
    };
    
    const command = commandMap[promptLabel];
    if (command) {
      handleSendMessage(command);
    } else {
      handleSendMessage(promptLabel);
    }
  };
  
  // Level-2 selection handlers
  const handleExport = (sections: string[], range: string) => {
    const sectionsStr = sections.join(',');
    handleSendMessage(`/export_pdf sections=[${sectionsStr}] range=${range}`);
    setExportBuilderOpen(false);
  };

  const handleExportCsv = (dataset: string, range: string) => {
    handleSendMessage(`/export_csv dataset=${dataset} range=${range}`);
    setExportCsvBuilderOpen(false);
  };

  const handleReviseNote = (instruction: string) => {
    const escaped = instruction.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    handleSendMessage(`/revise_note "${escaped}"`);
    setReviseNoteOpen(false);
  };

  const handleAssessmentTrend = (metric: string, range: string) => {
    handleSendMessage(`/assessment_trend ${metric} range=${range}`);
    setAssessmentTrendBuilderOpen(false);
  };

  const handleDraftNoteSelect = (format: string) => {
    handleSendMessage(`/draft_note format=${format}`);
    setDraftNoteOptionsOpen(false);
  };
  
  const handleDraftPlanSelect = (focus: string) => {
    handleSendMessage(`/draft_plan focus=${focus}`);
    setDraftPlanOptionsOpen(false);
  };
  
  const handleSimulationGenerate = (command: string) => {
    handleSendMessage(command);
    setSimulationPresetsOpen(false);
  };

  const handlePatientChange = (patient: any) => {
    // SMART-launched clinical view: the patient is fixed by the SMART context and
    // must not be switched from the UI. Ignore any attempt to change it.
    if (isSmartModeActive()) {
      setPatientPickerOpen(false);
      return;
    }
    setCurrentPatient(patient);

    // Find an existing conversation for this patient (most recent), or create one
    if (patient?.name) {
      const byPatient = thread.threads
        .filter(
          (t) =>
            (t.patientId && t.patientId === patient.mrn) ||
            (!t.patientId && t.patientName && t.patientName === patient.name)
        )
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      const existing = byPatient[0];

      if (existing) {
        // Jump straight into that patient's latest conversation (only clear input if switching thread)
        thread.updateThreadPatient(existing.id, { name: patient.name, mrn: patient.mrn });
        if (existing.id !== thread.currentThreadId) {
          thread.selectThread(existing.id);
          setInputValue('');
        }
      } else {
        // Start a fresh conversation for this patient
        thread.createThread({ patientName: patient.name, patientId: patient.mrn });
        setInputValue('');
      }
    }

    setPatientPickerOpen(false);
  };

  const sidebarOpen = sidebarPanel !== null;

  // Chat is only allowed when the current conversation is tied to a real patient (no placeholder).
  const currentThread = thread.currentThread;
  const hasPatientOnThread =
    currentThread &&
    (!!currentThread.patientId ||
      (!!currentThread.patientName &&
        currentThread.patientName.trim() !== 'No patient selected'));
  const canChat = !!hasPatientOnThread;

  const handleSavePromptFromMessage = (title: string, prompt: string) => {
    const alreadySaved = savedPrompts.prompts.some((p) => p.prompt === prompt.trim());
    if (alreadySaved) return;
    savedPrompts.save(title, prompt);
    toast.success('Added to saved prompts');
  };

  const handleRemoveSavedFromMessage = (promptText: string) => {
    const found = savedPrompts.prompts.find((p) => p.prompt === promptText);
    if (found) {
      savedPrompts.remove(found.id);
      toast.success('Removed from saved prompts');
    }
  };

  const clinicalWorkspaceMain = (
            <main
              id="main-content"
              tabIndex={-1}
              className="flex h-full min-w-0 min-h-0 flex-col bg-background outline-hidden"
            >
              {/* A slim strip, not a title block.
                  This band used to spend two lines of vertical space on
                  "CLINICAL WORKSPACE / Primary output and reasoning" — a label
                  that names the thing the clinician is already looking at and
                  tells them nothing they can act on. Stacked under the top bar
                  and (with the workspace IA on) the patient band, it was the
                  third strip of chrome before any content. One line now: what
                  this column is, and the way out of it. */}
              <div className="border-b border-border bg-background px-4 py-1.5 sm:px-6">
                <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-3">
                  <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-text-tertiary">Copilot</span>
                  <button
                    type="button"
                    onClick={() => setSidebarPanel('threads')}
                    className="flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-border bg-surface-sunken/40 px-2.5 text-[11px] font-medium text-text-tertiary transition-colors hover:bg-surface-sunken/70 hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="Open your conversations"
                  >
                    <HistoryIcon className="size-3.5" aria-hidden />
                    <span className="hidden sm:inline">Conversations</span>
                    {thread.visibleThreads.length > 0 ? <span className="tabular-nums">· {thread.visibleThreads.length}</span> : null}
                  </button>
                </div>
              </div>

              {entityFetchError && (
                <div className="border-b border-(--status-attention)/20 bg-status-attention-bg px-4 py-2 text-[13px] text-status-attention-text sm:px-6">
                  <div className="flex items-center justify-between gap-3">
                    <span className="min-w-0">{entityFetchError}</span>
                    <button
                      type="button"
                      onClick={() => setEntityFetchError(null)}
                      className="shrink-0 text-[11px] font-medium text-status-attention-text"
                      aria-label="Dismiss"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              )}

              <Transcript
                key={thread.currentThreadId}
                ref={transcriptRef}
                messages={thread.messages}
                density={settings.density}
                showEvidence={settings.evidenceFooters}
                isCommandLoading={commandLoading}
                pendingCommand={pendingCommand}
                onRetryCommand={handleSendMessage}
                onSendSuggestion={handleSendMessage}
                onClinicalQualityRerun={(userText) =>
                  handleSendMessage(userText, {
                    clinical_quality_rerun: true,
                    clinical_quality_no_stream: true,
                  })
                }
                onSavePrompt={handleSavePromptFromMessage}
                onRemoveSavedPrompt={handleRemoveSavedFromMessage}
                isPromptSaved={(prompt) => savedPrompts.prompts.some((p) => p.prompt === prompt)}
              />

              <div className="shrink-0 border-t border-border bg-background pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                <Composer
                  value={inputValue}
                  onChange={handleInputChange}
                  onSend={handleSendMessage}
                  onQuickPrompt={handleQuickPrompt}
                  simulationBuilderOpen={simulationPresetsOpen}
                  onSimulationGenerate={handleSimulationGenerate}
                  onSimulationBuilderClose={() => setSimulationPresetsOpen(false)}
                  commandLoading={commandLoading}
                  onStop={() => void handleStopStreaming('user')}
                  canChat={canChat}
                  assessmentsFromApi={assessmentsFromApi}
                  alertsFromApi={alertsFromApi}
                  redFlagsFromApi={redFlagsFromApi}
                  exportsFromApi={exportsFromApi}
                  simulationsFromApi={simulationsFromApi}
                  visitsFromApi={visitsFromApi}
                  onOpenSelectPatient={() => {
                    setPatientPickerForNewThread(false);
                    setPatientPickerOpen(true);
                  }}
                  onOpenExportBuilder={() => setExportBuilderOpen(true)}
                  onOpenExportCsvBuilder={() => setExportCsvBuilderOpen(true)}
                  onOpenReviseNoteModal={() => setReviseNoteOpen(true)}
                  onOpenAssessmentTrendBuilder={() => setAssessmentTrendBuilderOpen(true)}
                  onOpenPromptGallery={() => setPromptGalleryOpen(true)}
                />
              </div>
            </main>
  );

  return (
    <ThemeProvider>
      <TooltipProvider>
        {/* Toaster is mounted once, in main.tsx (top-center). A second one here
            rendered every toast twice, in two places. */}
        <div
          ref={appShellRef}
          className="h-screen flex overflow-hidden bg-background"
        >
      {/* Overlay drawers stay mounted; `open` drives the Radix close sequence
          (exit animation + focus restore to the trigger that opened them). */}
      <ThreadsDrawer
        open={sidebarPanel === 'threads'}
        onClose={() => setSidebarPanel(null)}
        threadsByPatient={thread.threadsByPatient}
        currentThreadId={thread.currentThreadId}
        currentThread={thread.currentThread}
        currentPatient={currentPatient.name?.trim() ? { name: currentPatient.name, mrn: currentPatient.mrn, status: currentPatient.status, risk: currentPatient.risk } : null}
        onSelectThread={handleSelectThread}
        onCreateThread={handleCreateThread}
        onCreateThreadForPatient={handleCreateThreadForPatient}
        onOpenPatientPickerForNewThread={() => {
          setPatientPickerForNewThread(true);
          setPatientPickerOpen(true);
        }}
        onDeleteThread={thread.deleteThread}
      />

      <PatientSheet
        open={sidebarPanel === 'patient'}
        onClose={() => setSidebarPanel(null)}
        patientName={currentPatient.name}
        patientMrn={currentPatient.mrn}
      />

      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        onSettingsChange={setSettings}
        commandApiEnabled={isCommandApiEnabled()}
        commandApiStatus={commandApiStatus}
        onCheckCommandApi={runHealthCheck}
      />

      <div className="flex-1 flex min-w-0 min-h-0 flex-col bg-background">
        <Header
          commandApiEnabled={isCommandApiEnabled()}
          commandApiStatus={commandApiStatus}
          patientName={currentPatient.name}
          patientMrn={currentPatient.mrn}
          patientId={currentPatient.mrn}
          hideAdminLink={isMobileViewport}
          onOpenPatientSheet={() => setSidebarPanel('patient')}
          onOpenSettings={() => setSettingsOpen(true)}
        />

        {/* The main page is the CHAT. Nothing else.
            It used to split in two whenever a patient was active: an eight-tab
            data column on the left and the transcript squeezed into a docked
            column on the right, under three stacked bands of chrome. The chat
            is the work this page exists for, and it was the smaller half of its
            own screen.

            The patient's chart has its own destinations and keeps them -- the
            digital twin page (organ health, social context, trajectories,
            lifecycle) from the top bar, and the patient sheet (twin summary +
            connected record) from the patient's name. Neither is reached
            through this column, so removing it takes nothing away from them.

            PatientFrame and its tabs are left in the tree rather than deleted:
            placement was what was wrong, and two of those tabs (Conditions,
            Risk factors) currently have no other home -- see the note in the
            PR. Throwing away working surfaces over a layout decision is how
            capability quietly disappears. */}
        <div className="flex-1 min-h-0 overflow-hidden">{clinicalWorkspaceMain}</div>
        </div>
      </div>

      <PatientPickerModal
        open={patientPickerOpen}
        onClose={() => {
          setPatientPickerOpen(false);
          setPatientPickerForNewThread(false);
        }}
        onSelectPatient={(patient) => {
          if (patientPickerForNewThread) {
            handleCreateThreadForPatient(patient);
            setPatientPickerForNewThread(false);
            setPatientPickerOpen(false);
          } else {
            handlePatientChange(patient);
          }
        }}
      />

      
      {/* Level-2 Palettes */}
      {exportBuilderOpen && (
        <ExportBuilder
          onExport={handleExport}
          onClose={() => setExportBuilderOpen(false)}
        />
      )}

      {exportCsvBuilderOpen && (
        <ExportCsvBuilder
          onExport={handleExportCsv}
          onClose={() => setExportCsvBuilderOpen(false)}
        />
      )}

      {reviseNoteOpen && (
        <ReviseNoteModal
          onSubmit={handleReviseNote}
          onClose={() => setReviseNoteOpen(false)}
        />
      )}

      {assessmentTrendBuilderOpen && (
        <AssessmentTrendBuilder
          onSubmit={handleAssessmentTrend}
          onClose={() => setAssessmentTrendBuilderOpen(false)}
        />
      )}
      
      {draftNoteOptionsOpen && (
        <QuickSelectionPalette
          title="Draft note"
          subtitle="Select note format"
          options={DRAFT_NOTE_OPTIONS}
          onSelect={handleDraftNoteSelect}
          onClose={() => setDraftNoteOptionsOpen(false)}
        />
      )}
      
      {draftPlanOptionsOpen && (
        <QuickSelectionPalette
          title="Draft plan"
          subtitle="Select plan focus"
          options={DRAFT_PLAN_OPTIONS}
          onSelect={handleDraftPlanSelect}
          onClose={() => setDraftPlanOptionsOpen(false)}
        />
      )}
      
      {alertsOptionsOpen && (
        <QuickSelectionPalette
          title="Alerts"
          subtitle="Filter by acknowledgment status"
          options={[
            { id: 'unacknowledged', label: 'Unacknowledged only' },
            { id: 'acknowledged', label: 'Acknowledged only' },
            { id: 'all', label: 'All alerts' }
          ]}
          onSelect={(id) => {
            const options = id === 'acknowledged' ? { acknowledged: true } : id === 'all' ? { showAll: true } : undefined;
            handleSendMessage('/alerts', options);
            setAlertsOptionsOpen(false);
          }}
          onClose={() => setAlertsOptionsOpen(false)}
        />
      )}
      
      <PromptGallery
        open={promptGalleryOpen}
        onClose={() => setPromptGalleryOpen(false)}
        savedPrompts={savedPrompts.prompts}
        onUsePrompt={handlePromptFromGallery}
        onSavePrompt={(title, prompt, category) => savedPrompts.save(title, prompt, category)}
        onRemoveSaved={savedPrompts.remove}
        isSaved={(promptText) => savedPrompts.prompts.some((p) => p.prompt === promptText)}
      />
      </TooltipProvider>
    </ThemeProvider>
  );
}

